// src/features/Navigation/components/NavigationEditPanel/NavigationEditPanel.tsx
import { useEffect, useRef, useState } from "react";
import {
	FormProvider,
	useForm,
	useFormState,
	Controller
} from "react-hook-form";
import { ArrowLeft, Upload } from "lucide-react";
import { Input } from "src/components/ui/input";
import { Textarea } from "src/components/ui/textarea";
import { Switch } from "src/components/ui/switch";
import { Label } from "src/components/ui/label";
import { Button } from "src/components/ui/button";
import { useAzureFileUpload } from "src/components/AssetUploaders/useAzureFileUpload";
import { AzureImage } from "src/components/AssetUploaders/AzureImage";
import LinkPickerStep from "src/components/LinkPicker/LinkPickerStep";

interface NavigationEditPanelProps {
	item: NavigationItem | null;
	allItems: NavigationItem[];
	onSave: (
		values: NavigationItemFormValues,
		imageReference: string | null,
		imageRemoved: boolean
	) => void;
	onDelete: (id: string) => void;
	onClose: () => void;
	isSaving: boolean;
}

function getDescendantIds(
	itemId: string,
	items: NavigationItem[]
): Set<string> {
	const ids = new Set<string>();
	const collectChildren = (parentId: string) => {
		for (const item of items) {
			if (item.parent === parentId && !ids.has(item.id)) {
				ids.add(item.id);
				collectChildren(item.id);
			}
		}
	};
	collectChildren(itemId);
	return ids;
}

const ALLOWED_IMAGE_TYPES = ["image/jpeg", "image/jpg", "image/png"];

const NavigationEditPanel = ({
	item,
	allItems,
	onSave,
	onDelete,
	onClose,
	isSaving
}: NavigationEditPanelProps) => {
	const [imageReference, setImageReference] = useState<string | null>(null);
	const [previewBlobUrl, setPreviewBlobUrl] = useState<string | null>(null);
	const [imageRemoved, setImageRemoved] = useState(false);
	const [imageError, setImageError] = useState<string | null>(null);
	const fileInputRef = useRef<HTMLInputElement>(null);

	const {
		upload,
		uploading: isUploading,
		error: uploadError
	} = useAzureFileUpload((data) => {
		if (Array.isArray(data) && typeof data[0] === "string") {
			setImageReference(data[0]);
		}
	});

	useEffect(() => {
		if (uploadError) setImageError("Failed to upload image, try again.");
	}, [uploadError]);

	const isNew = !item?.id;

	// LinkPickerStep keys "hashtag selected" off the populated selectedFeed
	// object, not the bare UUID. If the backend returns a feed_id without a
	// usable feed name (e.g. the linked hashtag was deleted or the embed
	// didn't hydrate), treat the link as broken: drop feedId too so the
	// picker honestly shows "No link" and a save doesn't silently re-emit
	// the dangling id.
	const resolvedFeed =
		item?.feed && item.feed.name
			? { uuid: item.feed.uuid, name: item.feed.name }
			: null;

	const defaultValues: NavigationItemFormValues = {
		name: item?.name ?? "",
		description: item?.description ?? "",
		permalink: item?.permalink ?? "",
		parent: item?.parent ?? "",
		url: item?.url ?? "",
		web: item?.web ?? false,
		feedId: resolvedFeed ? (item?.feedId ?? null) : null,
		selectedFeed: resolvedFeed,
		published: item?.published ?? false,
		layout: item?.layout ?? "row"
	};

	const formMethods = useForm<NavigationItemFormValues>({
		defaultValues,
		mode: "onChange"
	});
	const { control, handleSubmit, register, reset, watch } = formMethods;

	const { isDirty, errors } = useFormState({ control });

	// Reset form when selected item changes
	useEffect(() => {
		reset(defaultValues);
		setImageReference(null);
		setImageRemoved(false);
		setImageError(null);
		setPreviewBlobUrl((prev) => {
			if (prev) URL.revokeObjectURL(prev);
			return null;
		});
	}, [item?.id, item?.parent]);

	// Clean up preview URL
	useEffect(() => {
		if (imageReference && previewBlobUrl) {
			URL.revokeObjectURL(previewBlobUrl);
			setPreviewBlobUrl(null);
		}
	}, [imageReference, previewBlobUrl]);

	const validateAndSetFile = (file: File) => {
		if (!ALLOWED_IMAGE_TYPES.includes(file.type)) {
			setImageError("Only JPG and PNG files are allowed.");
			setImageReference(null);
			if (previewBlobUrl) URL.revokeObjectURL(previewBlobUrl);
			setPreviewBlobUrl(null);
			if (fileInputRef.current) fileInputRef.current.value = "";
			return;
		}
		setImageError(null);
		if (previewBlobUrl) URL.revokeObjectURL(previewBlobUrl);
		setPreviewBlobUrl(URL.createObjectURL(file));
		setImageRemoved(false);
		upload(file, "images", undefined, "admin-content-image");
	};

	const handleRemoveImage = () => {
		setImageReference(null);
		if (previewBlobUrl) URL.revokeObjectURL(previewBlobUrl);
		setPreviewBlobUrl(null);
		setImageRemoved(true);
		setImageError(null);
		if (fileInputRef.current) fileInputRef.current.value = "";
	};

	const onSubmit = (values: NavigationItemFormValues) => {
		onSave(values, imageReference, imageRemoved);
	};

	// Filter parent options: exclude self and descendants
	const excludeIds = item?.id
		? new Set([item.id, ...getDescendantIds(item.id, allItems)])
		: new Set<string>();
	const parentOptions = allItems.filter((i) => !excludeIds.has(i.id));

	const existingImageRef = item?.image && !imageRemoved ? item.image : null;
	const hasImage =
		!imageRemoved &&
		(!!imageReference || !!previewBlobUrl || !!existingImageRef);

	const preventEnterSubmit = (e: React.KeyboardEvent) => {
		const target = e.target as HTMLElement;
		if (e.key === "Enter" && target.tagName === "INPUT") e.preventDefault();
	};

	const canSave = isNew
		? true
		: isDirty || imageReference !== null || imageRemoved;

	const renderImagePreview = () => {
		if (previewBlobUrl) {
			return (
				<img
					src={previewBlobUrl}
					alt="Uploaded image preview"
					className="object-contain max-w-full max-h-full"
				/>
			);
		}
		const ref = imageReference ?? existingImageRef;
		if (ref) {
			return (
				<AzureImage
					uuid={ref}
					imageProfile="admin-content-image"
					alt="Uploaded image preview"
					className="object-contain max-w-full max-h-full"
				/>
			);
		}
		return null;
	};

	return (
		<FormProvider {...formMethods}>
			<div className="h-full flex flex-col gap-4">
				<div className="flex items-center gap-3 h-9">
					<button
						onClick={onClose}
						className="inline-flex items-center gap-1.5 text-sm text-zinc-600 hover:text-foreground transition-colors"
					>
						<ArrowLeft className="h-4 w-4" />
					</button>
					<label className="font-heading text-lg font-medium text-foreground">
						{isNew ? "New Item" : "Edit Item"}
					</label>
				</div>

				<form
					onSubmit={handleSubmit(onSubmit)}
					onKeyDown={preventEnterSubmit}
					className={[
						"flex-1 overflow-y-auto rounded-xl border bg-subtle shadow-sm flex flex-col transition-colors",
						errors.name ? "border-red-400/60" : "border-border"
					].join(" ")}
				>
					{errors.name && (
						<div className="relative">
							<span
								role="alert"
								className="absolute -top-2.5 right-3 bg-red-500 text-white text-xs px-2 py-0.5 rounded-full font-medium"
							>
								{errors.name.message}
							</span>
						</div>
					)}
					<div className="p-6 flex flex-col gap-4">
						{/* Name */}
						<div>
							<Label
								htmlFor="name"
								className="text-sm font-medium text-zinc-600 mb-1.5 block"
							>
								Name *
							</Label>
							<Input
								id="name"
								{...register("name", {
									required: "Name is required",
									maxLength: {
										value: 255,
										message: "Name must be 255 characters or fewer"
									}
								})}
								placeholder="Menu item label"
								className="bg-white"
							/>
						</div>

						{/* Description */}
						<div>
							<Label
								htmlFor="description"
								className="text-sm font-medium text-zinc-600 mb-1.5 block"
							>
								Description
							</Label>
							<Textarea
								id="description"
								{...register("description", {
									maxLength: {
										value: 255,
										message: "Description must be 255 characters or fewer"
									}
								})}
								placeholder="Optional subtitle text"
								rows={2}
								className="bg-white"
							/>
						</div>

						{/* Parent */}
						<div>
							<Label className="text-sm font-medium text-zinc-600 mb-1.5 block">
								Parent
							</Label>
							<select
								{...register("parent")}
								className="flex h-9 w-full items-center rounded-md border border-border bg-white px-3 py-2 text-sm shadow-sm focus:outline-none focus:ring-1 focus:ring-ring"
							>
								<option value="">None (top level)</option>
								{parentOptions.map((opt) => (
									<option key={opt.id} value={opt.id}>
										{opt.name}
									</option>
								))}
							</select>
						</div>

						{/* Layout — only shown for top-level items */}
						{!watch("parent") && (
							<div>
								<Label className="text-sm font-medium text-zinc-600 mb-1.5 block">
									Layout
								</Label>
								<select
									{...register("layout")}
									className="flex h-9 w-full items-center rounded-md border border-border bg-white px-3 py-2 text-sm shadow-sm focus:outline-none focus:ring-1 focus:ring-ring"
								>
									<option value="row">Row</option>
									<option value="grid">Grid</option>
								</select>
							</div>
						)}

						{/* Link — hidden only for top-level grid containers */}
						{!(watch("layout") === "grid" && !watch("parent")) && (
							<div>
								<Label className="text-sm font-medium text-zinc-600 mb-1.5 block">
									Link
								</Label>
								<p className="text-[13px] text-muted-foreground mb-2">
									Leave as &quot;No link&quot; to use this item as a parent
									label
								</p>
								<LinkPickerStep
									urlName="url"
									feedName="feedId"
									selectedFeedName="selectedFeed"
									openInBrowserName="web"
									hideLabel
								/>
							</div>
						)}

						{/* Published */}
						<div className="flex items-center gap-3">
							<Controller
								control={control}
								name="published"
								render={({ field }) => (
									<Switch
										id="published"
										checked={field.value}
										onCheckedChange={field.onChange}
									/>
								)}
							/>
							<Label
								htmlFor="published"
								className="text-sm font-medium text-zinc-600 cursor-pointer"
							>
								Published
							</Label>
						</div>

						{/* Image upload — only shown for children of a grid parent */}
						{watch("parent") &&
							allItems.find((i) => i.id === watch("parent"))?.layout ===
								"grid" && (
								<div>
									<Label className="text-sm font-medium text-zinc-600 mb-1.5 block">
										Image
									</Label>
									<p className="text-[13px] text-muted-foreground mb-2">
										JPG or PNG, 513x300px recommended
									</p>
									<input
										ref={fileInputRef}
										type="file"
										accept="image/jpeg,image/jpg,image/png"
										className="hidden"
										aria-label="Upload navigation image"
										onChange={(e) => {
											const file = e.target.files?.[0];
											if (file) validateAndSetFile(file);
										}}
									/>
									{hasImage ? (
										<>
											<div className="mb-3 rounded-lg overflow-hidden border border-border bg-subtle h-[200px] flex items-center justify-center">
												{renderImagePreview()}
											</div>
											{isUploading && (
												<p className="text-xs text-muted-foreground mb-2">
													Uploading...
												</p>
											)}
											<div className="flex items-center gap-2">
												<button
													type="button"
													className="inline-flex items-center justify-center px-5 h-9 rounded-lg border border-foreground bg-white text-foreground text-sm font-medium cursor-pointer hover:bg-foreground hover:text-white transition-colors"
													onClick={() => fileInputRef.current?.click()}
												>
													Change image
												</button>
												<button
													type="button"
													className="inline-flex items-center justify-center px-5 h-9 rounded-lg border border-red-300 bg-white text-red-600 text-sm font-medium cursor-pointer hover:bg-red-600 hover:text-white hover:border-red-600 transition-colors"
													onClick={handleRemoveImage}
												>
													Remove
												</button>
											</div>
										</>
									) : (
										<div
											role="button"
											tabIndex={0}
											onClick={() => fileInputRef.current?.click()}
											onKeyDown={(e) => {
												if (e.key === "Enter" || e.key === " ") {
													e.preventDefault();
													fileInputRef.current?.click();
												}
											}}
											onDragOver={(e) => {
												e.preventDefault();
												e.stopPropagation();
											}}
											onDrop={(e) => {
												e.preventDefault();
												e.stopPropagation();
												const file = e.dataTransfer.files[0];
												if (file) validateAndSetFile(file);
											}}
											className="flex flex-col items-center justify-center gap-3 rounded-lg border-2 border-dashed px-6 py-10 text-center transition-colors cursor-pointer border-border bg-white hover:border-primary-light hover:bg-primary-lighter/50"
										>
											<Upload
												className="h-6 w-6 text-muted-foreground"
												aria-hidden="true"
											/>
											<div>
												<p className="text-sm font-medium text-foreground">
													Drag image here or click to browse
												</p>
												<p className="text-xs text-muted-foreground mt-1">
													JPG or PNG
												</p>
											</div>
										</div>
									)}
									{imageError && (
										<p role="alert" className="text-sm text-red-500 mt-2">
											{imageError}
										</p>
									)}
								</div>
							)}
					</div>
					{/* Actions */}
					<div className="flex items-center justify-between p-6 border-t border-border">
						{!isNew && (
							<Button
								type="button"
								variant="destructive"
								onClick={() => onDelete(item!.id)}
							>
								Delete
							</Button>
						)}
						<div className={isNew ? "ml-auto" : ""}>
							<Button
								type="submit"
								disabled={!canSave || isSaving || isUploading}
							>
								{isSaving ? (
									<span className="flex items-center gap-2">
										<span className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
										<span className="sr-only">Saving...</span>
										Saving...
									</span>
								) : isNew ? (
									"Create"
								) : (
									"Save"
								)}
							</Button>
						</div>
					</div>
				</form>
			</div>
		</FormProvider>
	);
};

export default NavigationEditPanel;
